/**
 * The Shopping List (the Oct 21 mockup, "Ingredients & supplies · all stops"): one section, the stops' MILK first,
 * then the recipe ingredients by group, then SUPPLIES last. Recipe ingredient milk stays with the ingredients. Range
 * prints buy once.
 */

import { formatMeasureText, formatMeasureValue } from '@ordo/constants/measure-format'
import { isVolumeUnit, isWeightUnit } from '@ordo/constants/measures'

import { groupIngredientsForBuying } from '@/lib/chefs/ingredient-groups'
import { ingredientKey, mergeAllIngredients, type MergedIngredient } from '@/lib/chefs/ingredients'
import { isGenericCountUnit, pluralizeNoun } from '@/lib/chefs/plural'
import { packetSupplyRows } from '@/lib/chefs/print-packages'
import type { Campus, CampusMilkLine } from '@/lib/chefs/types'

import { KITCHEN_COPY, type PacketDay, packetRangeDateBox } from './day'
import { type PacketSection, type PacketUnit, repeatOf } from './units'

interface Usage {
  foods: string[]
  /** The stop numbers, for a bought-and-served item on a one-day list. */
  stops: string[]
}

function note(usage: Map<string, Usage>, key: string, food: string | null, stop: string | null) {
  const entry = usage.get(key) ?? { foods: [], stops: [] }
  if (food && !entry.foods.includes(food)) {
    entry.foods.push(food)
  }
  if (stop && !entry.stops.includes(stop)) {
    entry.stops.push(stop)
  }
  usage.set(key, entry)
}

/**
 * Who calls for each merged ingredient: the recipe foods for a recipe
 * ingredient, the stops for a bought-and-served item. Keyed like
 * `mergeAllIngredients` so the two line up. The stops' milk is not here: it
 * has its own table (`milkBuyRows`).
 */
export function ingredientUsage(days: readonly PacketDay[]): Map<string, Usage> {
  const usage = new Map<string, Usage>()
  for (const day of days) {
    for (const stop of day.stops) {
      const number = days.length === 1 ? stop.identity.number : null
      for (const c of stop.campus.mealtimes.flatMap((block) => block.deliverables.flatMap((d) => d.components))) {
        if (!c.recipe) {
          note(usage, ingredientKey(c.name, 'each', null), null, number)
          continue
        }
        for (const ing of c.recipe.ingredients) {
          if (ing.quantity !== null && ing.unit !== null) {
            note(usage, ingredientKey(ing.name, ing.unit, ing.brand ?? null), c.name, null)
          }
        }
      }
    }
  }
  return usage
}

/** "Stops 1, 2" or the foods, at most three ("BBQ Chicken, Mac, Salad +2"). */
function usedForWords(usage: Usage | undefined): string | null {
  if (!usage) {
    return null
  }
  if (usage.foods.length > 0) {
    const shown = usage.foods.slice(0, 3).join(', ')
    return usage.foods.length > 3 ? `${shown} +${usage.foods.length - 3}` : shown
  }
  return usage.stops.length > 0 ? `${usage.stops.length === 1 ? 'Stop' : 'Stops'} ${usage.stops.join(', ')}` : null
}

/** The Buy column: kitchen units, one term ("49 lb", "5.9 gal", "181 each"). */
export function buyWords(row: Pick<MergedIngredient, 'quantity' | 'unit'>): string {
  return formatMeasureText(row.quantity, row.unit, { system: 'imperial', use: 'PURCHASING' })
}

/** The Ordo amount column: the recipes' total in their own unit, or null when it reads the same as Buy. */
export function ordoAmountWords(row: Pick<MergedIngredient, 'quantity' | 'unit'>, buy: string): string | null {
  const own = `${formatMeasureValue(row.quantity)} ${row.unit}`.trim()
  return own === buy ? null : own
}

/** How much of one milk to buy, two ways at one weight (Abner, Oct 19: "show both options, with no prevalence to any"). */
export interface MilkBuyRow {
  /** Total demand in the recipe ingredient’s original unit. */
  purchase: string | null
  key: string
  name: string
  /** "680 cartons": the servings counted in the package one serving is; null for a poured serving. */
  count: string | null
  /** "42.5 gal": the milk its recipe pours, in kitchen units; null without a volume recipe. */
  volume: string | null
  /** "Stops 1, 2" on a one-day list. */
  usedFor: string | null
  /** The servings the stops take: what to buy when neither a count nor a volume can be said. */
  servings: number
}

/** "680 cartons" when every line's serving is a whole package of one kind; null for a poured or mixed serving. */
function milkCount(lines: readonly CampusMilkLine[]): string | null {
  const units = new Set(lines.map((line) => line.component.portion?.unit.trim().toLowerCase() ?? ''))
  const [unit] = [...units]
  if (units.size !== 1 || !unit || isVolumeUnit(unit) || isWeightUnit(unit)) {
    return null
  }
  const n = Math.ceil(
    lines.reduce(
      (sum, line) => sum + line.qty * line.component.portion!.quantity * (line.component.servingMultiplier ?? 1),
      0,
    ),
  )
  return isGenericCountUnit(unit) ? n.toLocaleString() : `${n.toLocaleString()} ${pluralizeNoun(unit, n)}`
}

/** "42.5 gal": the milk the lines' recipes pour, through the shopping list's own merge; null without one. */
function milkVolume(lines: ReadonlyArray<{ campus: Campus; line: CampusMilkLine }>): string | null {
  const rows = mergeAllIngredients(lines.map(({ campus, line }) => ({ ...campus, mealtimes: [], milk: [line] })))
  const volumes = rows.filter((row) => isVolumeUnit(row.unit)).map(buyWords)
  return volumes.length > 0 ? volumes.join(' + ') : null
}

function milkRecipePurchase(lines: ReadonlyArray<{ campus: Campus; line: CampusMilkLine }>): string | null {
  const rows = mergeAllIngredients(
    lines
      .filter(({ line }) => line.component.recipe)
      .map(({ campus, line }) => ({ ...campus, mealtimes: [], milk: [line] })),
  )
  return rows.length ? rows.map((row) => `${formatMeasureValue(row.quantity)} ${row.unit}`).join(' + ') : null
}

/** How much of each milk the days' stops take, one row per kind, the most first: the Shopping List's MILK. */
export function milkBuyRows(days: readonly PacketDay[]): MilkBuyRow[] {
  const kinds = new Map<string, { lines: Array<{ campus: Campus; line: CampusMilkLine }>; stops: string[] }>()
  for (const day of days) {
    for (const stop of day.stops) {
      for (const line of stop.campus.milk ?? []) {
        const kind = kinds.get(line.name) ?? { lines: [], stops: [] }
        kind.lines.push({ campus: stop.campus, line })
        if (days.length === 1 && !kind.stops.includes(stop.identity.number)) {
          kind.stops.push(stop.identity.number)
        }
        kinds.set(line.name, kind)
      }
    }
  }
  const servings = (kind: { lines: Array<{ line: CampusMilkLine }> }) =>
    kind.lines.reduce((sum, { line }) => sum + line.qty, 0)
  return [...kinds.entries()]
    .sort(([a, x], [b, y]) => servings(y) - servings(x) || a.localeCompare(b))
    .map(([name, kind]) => ({
      key: name,
      name,
      purchase: milkRecipePurchase(kind.lines),
      count: milkCount(kind.lines.map(({ line }) => line)),
      volume: milkVolume(kind.lines),
      usedFor: kind.stops.length > 0 ? `${kind.stops.length === 1 ? 'Stop' : 'Stops'} ${kind.stops.join(', ')}` : null,
      servings: servings(kind),
    }))
}

/** The supplies: containers, packaging and disposables, with each stop's share on a one-day list. */
function supplyRows(days: readonly PacketDay[]) {
  const campuses = days.flatMap((day) => day.campuses)
  const perStop = new Map<string, string[]>()
  if (days.length === 1) {
    for (const stop of days[0]!.stops) {
      for (const row of packetSupplyRows([stop.campus])) {
        const list = perStop.get(row.key) ?? []
        list.push(`Stop ${stop.identity.number}: ${row.qty.toLocaleString()}`)
        perStop.set(row.key, list)
      }
    }
  }
  return packetSupplyRows(campuses).map((row) => ({
    key: row.key,
    name: row.name,
    buy: row.qty.toLocaleString(),
    usedFor: perStop.get(row.key)?.join(' · ') ?? null,
    tag: row.isCompostable ? 'COMPOSTABLE' : null,
  }))
}

export function shoppingListSectionId(dayIso: string | null): string {
  return `${dayIso ?? 'range'}|shopping`
}

/**
 * The Shopping List for the given days: one day's list, or (`range`) the whole print's.
 * Null when nothing needs buying. The date is the date stamp's, top right like
 * every other page (Hunter, Oct 20: "you could probably put the date thing on
 * the top right like the other ones have"); a multi-day list's stamp spans
 * the range.
 */
export function shoppingListSection(
  days: readonly PacketDay[],
  // The caterer's name no longer prints on the shopping list (the Oct 21 canvas: only the kitchen stop sheet says it).
  _kitchenName: string,
  range: boolean,
): PacketSection | null {
  const dayIso = range ? null : (days[0]?.iso ?? null)
  const sid = shoppingListSectionId(dayIso)
  const usage = ingredientUsage(days)
  const campuses = days.flatMap((day) => day.campuses)
  const milk = milkBuyRows(days)
  const foods = mergeAllIngredients(campuses.map((campus) => ({ ...campus, milk: [] })))
  const groups = groupIngredientsForBuying(foods, (row) => ({
    name: row.name,
    category: row.category ?? 'UNCATEGORIZED',
    usdaCategory: row.usdaCategory ?? null,
  }))
  const supplies = supplyRows(days)
  if (milk.length === 0 && foods.length === 0 && supplies.length === 0) {
    return null
  }

  const units: PacketUnit[] = []
  const head: Extract<PacketUnit, { kind: 'shop-head' }> = {
    id: `${sid}|head`,
    group: null,
    role: 'band',
    kind: 'shop-head',
  }
  // Repeat the same purchasing header on continuation pages.
  units.push(head)
  if (milk.length > 0) {
    const id = `${sid}|milk`
    units.push({ id: `${id}|band`, group: id, role: 'band', kind: 'band', text: 'MILK', note: null, level: 1 })
    for (const row of milk) {
      // Buy in the package a serving is ("345 cartons"), else by volume, else the servings; never a blank line. The
      // other way rides in the Amount column.
      const buy =
        row.count ??
        row.volume ??
        row.purchase ??
        `${row.servings.toLocaleString()} ${row.servings === 1 ? 'serving' : 'servings'}`
      units.push({
        id: `${id}|${row.key}`,
        group: id,
        role: 'row',
        kind: 'shop-row',
        name: row.name,
        buy,
        amount: row.count && row.volume ? row.volume : null,
        usedFor: row.usedFor,
        tag: null,
      })
    }
  }
  for (const group of groups) {
    const id = `${sid}|${group.label}`
    units.push({
      id: `${id}|band`,
      group: id,
      role: 'band',
      kind: 'band',
      text: group.label.toUpperCase(),
      note: null,
      level: 1,
    })
    for (const row of group.rows) {
      const buy = buyWords(row)
      units.push({
        id: `${id}|${row.key}`,
        group: id,
        role: 'row',
        kind: 'shop-row',
        name: row.name,
        brand: row.brand,
        buy,
        amount: ordoAmountWords(row, buy),
        usedFor: usedForWords(usage.get(row.key)),
        tag: null,
      })
    }
  }
  if (supplies.length > 0) {
    const id = `${sid}|supplies`
    units.push({
      id: `${id}|band`,
      group: id,
      role: 'band',
      kind: 'band',
      text: 'SUPPLIES',
      note: null,
      level: 1,
    })
    for (const row of supplies) {
      units.push({
        id: `${id}|${row.key}`,
        group: id,
        role: 'row',
        kind: 'shop-row',
        name: row.name,
        buy: row.buy,
        amount: null,
        usedFor: row.usedFor,
        tag: row.tag,
      })
    }
  }

  const firstDay = days[0]
  const lastDay = days[days.length - 1]
  return {
    id: sid,
    kind: 'shopping-list',
    dayIso,
    band: {
      name: 'INGREDIENTS',
      counter: null,
      right: KITCHEN_COPY,
      style: 'solid',
    },
    // Abner, Oct 20: the page's plain title and a short description of what it holds, no overhead line and no legend.
    // Matt's "ONE DAY ONLY" stays in the words. A week's list with a column per day is still open; a multi-day print
    // buys the whole range in one list, first.
    title: {
      eyebrow: 'INGREDIENTS & SUPPLIES · ALL STOPS',
      title: 'Shopping List',
      note: null,
      dateBox: firstDay && lastDay ? packetRangeDateBox(firstDay.date, lastDay.date) : null,
    },
    footer: {
      key: range ? 'range|ingredients' : `${firstDay?.iso ?? ''}|ingredients`,
      label: 'Shopping List',
      counted: false,
    },
    units,
    repeat: [repeatOf(head)],
    preferOnePage: false,
  }
}
